import { forwardRef, type HTMLAttributes } from 'react';
import { cn } from '../../lib/cn';
import { formatDuration, formatDurationShort } from '../../lib/format';
import { useCountdown } from '../../hooks/useCountdown';

export interface CountdownProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'children'> {
  /** Completion / arrival timestamp. */
  target: number | string | Date | null | undefined;
  /** `clock` → 00:04:12 (queues), `short` → 4h 12m (dense rows). */
  format?: 'clock' | 'short';
  /** Turn amber below this many seconds, red below a quarter of it. */
  urgentBelow?: number;
  /** Rendered once the countdown reaches zero. */
  doneLabel?: string;
  onComplete?: () => void;
}

/**
 * Live countdown readout.
 *
 * Always monospaced with tabular numerals, so the digits don't jitter as they
 * tick — a detail that matters a lot when a dozen of these sit in one queue.
 */
export const Countdown = forwardRef<HTMLSpanElement, CountdownProps>(function Countdown(
  { target, format = 'clock', urgentBelow = 0, doneLabel = 'Bereit', onComplete, className, ...rest },
  ref,
) {
  const remaining = useCountdown(target, onComplete);

  if (target === null || target === undefined || remaining <= 0) {
    return (
      <span
        ref={ref}
        className={cn('font-display text-label uppercase text-success', className)}
        {...rest}
      >
        {doneLabel}
      </span>
    );
  }

  const critical = urgentBelow > 0 && remaining <= urgentBelow / 4;
  const urgent = urgentBelow > 0 && remaining <= urgentBelow;

  return (
    <span
      ref={ref}
      data-sg-numeric
      className={cn(
        'tabular-nums',
        critical
          ? 'text-danger-bright'
          : urgent
            ? 'text-warning-bright'
            : 'text-content-secondary',
        className,
      )}
      {...rest}
    >
      {format === 'clock' ? formatDuration(remaining) : formatDurationShort(remaining)}
    </span>
  );
});
